import { Page, Header, Content, TableColumn, Link } from '@backstage/core-components';
import { createRoutableExtension } from '@backstage/core-plugin-api';
import {
    EntityListProvider,
    EntityKindPicker,
    EntityTypePicker,
    EntityOwnerPicker,
    EntityTagPicker,
    EntityLifecyclePicker,
    entityRouteRef,    
} from '@backstage/plugin-catalog-react';
import { 
    CatalogTable, 
    CatalogTableRow, 
    catalogPlugin, 
    DefaultCatalogPageProps } from '@backstage/plugin-catalog';
import { Card, Chip, IconButton, makeStyles, Typography } from '@material-ui/core';
import { ChevronRight, Person } from '@material-ui/icons';
import { generatePath } from 'react-router';


const useStyles = makeStyles({
    container: {
        display: 'grid',
        gap: '16px',
        gridTemplateColumns: 'repeat(4, 1fr)',
        alignItems: 'start',
    },
    cardContainer: {
        display: 'grid',
        gap: 32,
        marginTop: 24,
        justifyContent: 'space-between',
        gridTemplateColumns: 'repeat(3, 1fr)',
    },
    cardContent: {
        display: 'flex',
        gap: 32,
        flex: 1
    },
    card: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        alignItems: 'center',
        width: '#FFFFFF',
        background: '100%',
        boxShadow: '0 1px 6px 0 rgba(51, 51, 51, 0.16)',
        height: 120,
        padding: 16,
    },
    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 54,
        height: 54,
        borderRadius: '100%'
    },
    contentText: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        flex: 1
    },
    iconTitle: {
        fontSize: 16,
        fontWeight: 700
    },
    iconDescription: {
        fontSize: 11.67,
        fontWeight: 400
    },
    space: {
        gap: 16,
        display: 'flex',
        flexDirection: 'column'
    },
    flex: {
        gap: 4,
        display: 'flex',
        alignItems: 'center'
    }
});

type DashCardProps = {
    title: string;
    description: string;
    icon: React.ReactNode;
    href: string;
    color: string;
}

const DashCard = ({ title, description, color, icon, href }: DashCardProps) => {
    const classes = useStyles();

    return <div className={classes.card}>
        <div className={classes.cardContent}>
            <div className={classes.iconContainer} style={{ background: color }}>
                {icon}
            </div>
            <div className={classes.contentText}>
                <span className={classes.iconTitle}>{title}</span>
                <span className={classes.iconDescription}>{description}</span>
            </div>
        </div>
        <Link to={href}>
            <IconButton>
                <ChevronRight />
            </IconButton>
        </Link>
    </div>
}

// Definição das colunas da tabela
const columns: TableColumn<CatalogTableRow>[] = [
    {
        title: 'Nome',
        field: 'metadata.name',
        highlight: true,
        render: (data: CatalogTableRow) => {
            const kind = data.entity.kind.toLowerCase();
            const namespace = data.entity.metadata.namespace ?? 'default';
            const name = data.entity.metadata.name;

            // Link para a página do componente (EntityLayout)
            const path = generatePath('/catalog/:namespace/:kind/:name', {
                kind,
                namespace,
                name,
            });

            return <Link to={path}>{name}</Link>;
        },
        customFilterAndSearch: (query, row: CatalogTableRow) =>
            row.entity.metadata.name?.toLowerCase().includes(query.toLowerCase()),
    },
    {
        title: 'Tipo',
        field: 'spec.type',
        render: (data: CatalogTableRow) => (
            <Typography variant='caption' color='textPrimary'>
                {typeof data.entity.spec?.type === 'string' ? data.entity.spec.type : ''}
            </Typography>
        ),
        customFilterAndSearch: (query, row: CatalogTableRow) =>
            (typeof row.entity.spec?.type === 'string' ? row.entity.spec.type : '').toLowerCase().includes(query.toLowerCase()),
    },
    {
        title: 'Ambiente',
        field: 'spec.lifecycle',
        render: (data: CatalogTableRow) => (
            <Typography variant='caption' color='textPrimary'>
                {typeof data.entity.spec?.lifecycle === 'string' 
                    ? data.entity.spec.lifecycle.charAt(0).toUpperCase() + data.entity.spec.lifecycle.slice(1) 
                    : ''}
            </Typography>
        ),
        customFilterAndSearch: (query, row: CatalogTableRow) =>
            (typeof row.entity.spec?.lifecycle === 'string' ? row.entity.spec.lifecycle : '').toLowerCase().includes(query.toLowerCase()),
    },
    {
        title: 'Tags',
        render: (data: CatalogTableRow) => (
            <div className={useStyles().flex}>
                {data.entity.metadata.tags?.map(tag => (
                    <div key={tag}>
                        <Chip variant='outlined' size='small' label={tag} />
                    </div>
                ))}
            </div>
        ),
    },
    {
        title: 'Descrição',
        field: 'metadata.description',
        render: (data: CatalogTableRow) => (
            <Typography variant='caption'>{data.entity.metadata.description}</Typography>
        ),
        customFilterAndSearch: (query, row: CatalogTableRow) =>
            !!row.entity.metadata.description?.toLowerCase().includes(query.toLowerCase())
    },
    {
        title: 'Proprietário',
        field: 'spec.owner',
        render: (data: CatalogTableRow) => {
            const ownedByRelationsTitle = typeof data.resolved?.ownedByRelationsTitle === 'string' ? data.resolved.ownedByRelationsTitle : '';
            const entitySpecOwner = typeof data.entity.spec?.owner === 'string' ? data.entity.spec.owner : '';
            const ownerRef = entitySpecOwner;

            // Parse "kind:namespace/name" or "name" into a catalog path
            const buildOwnerPath = (ref: string): string => {
                // format: "kind:namespace/name" or "namespace/name" or "name"
                const withoutKind = ref.includes(':') ? ref.split(':')[1] : ref;
                const kind = ref.includes(':') ? ref.split(':')[0] : 'group';
                const parts = withoutKind.split('/');
                const namespace = parts.length > 1 ? parts[0] : 'default';
                const name = parts.length > 1 ? parts[1] : parts[0];
                return generatePath('/catalog/:namespace/:kind/:name', { namespace, kind, name });
            };

            const label = ownedByRelationsTitle ? ownedByRelationsTitle.toUpperCase() : ownerRef.toUpperCase();
            const path = ownerRef ? buildOwnerPath(ownerRef) : '';

            return (
                <div className={useStyles().flex}>
                    <Person />
                    {path ? (
                        <Link to={path}>
                            <Typography variant='caption'>{label}</Typography>
                        </Link>
                    ) : (
                        <Typography variant='caption'>{label}</Typography>
                    )}
                </div>
            );
        },
        customFilterAndSearch: (query, row: CatalogTableRow) =>
            (typeof row.entity.spec?.owner === 'string' ? row.entity.spec.owner : '').toLowerCase().includes(query.toLowerCase()),
    }
];

// Componente wrapper para consumir entidades do provider
const CatalogContent = () => {
    return (
        <CatalogTable
            columns={columns}
            tableOptions={{
                search: true,
                paging: true,              // ativa paginação
                pageSize: 10,              // quantidade inicial de linhas
                pageSizeOptions: [10, 20, 50], // opções no select
                emptyRowsWhenPaging: false,   // evita linhas em branco no fim
                searchFieldVariant: "outlined",
            }}
            actions={[]}
            title="Catálogo Disponível"
            emptyContent="Nenhuma entidade encontrada."
        />
    );
};

const CustomCatalogPage = () => {
    const classes = useStyles();

    return (
        <Page themeId="home">
            <Header
                title="Portal OLIMPIA"
            />
            <Content>
                <Typography variant='h3'>Catálogo de Componentes</Typography>
                <EntityListProvider>
                    <Card elevation={1} style={{ padding: 16, margin: '32px 0px', background: "#F0F0F0" }}>
                        <div className={classes.container}>
                            <EntityKindPicker hidden />
                            <EntityTypePicker />
                            <EntityLifecyclePicker />
                            <EntityTagPicker />
                            <EntityOwnerPicker />
                        </div>
                    </Card>
                    <CatalogContent />
                </EntityListProvider>
            </Content>
        </Page>
    );
};

export const CustomCatalogIndexPage: (props: DefaultCatalogPageProps) => JSX.Element =
  catalogPlugin.provide(
    createRoutableExtension({
      name: 'CatalogIndexPage',
      component: () => Promise.resolve(CustomCatalogPage),
      mountPoint: catalogPlugin.routes.catalogIndex,
    }),
);